/* Native Shopify forms stay the source of truth. Never bypass checkout safeguards. */
class PlentySweetTheme {
  constructor() {
    this.drawerState = null;
    this.restoredForms = new WeakSet();
    document.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-variant-select]')) this.updateVariant(event.target, true);
      if (event.target.matches('[data-cart-quantity]')) this.updateCartState(event.target.form);
    });
    document.addEventListener('input', (event) => {
      if (event.target.matches('[data-cart-quantity]')) this.updateCartState(event.target.form);
    });
    document.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('submit', (event) => this.onSubmit(event));
    document.addEventListener('shopify:section:load', (event) => this.init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      if (this.drawerState && event.target.contains(this.drawerState.drawer)) this.closeDrawer();
    });
    window.addEventListener('pageshow', () => this.init(document));
    window.addEventListener('pagehide', () => this.closeDrawer(false));
    window.matchMedia('(min-width: 64rem)').addEventListener('change', event => {
      if (event.matches) this.closeDrawer(false);
    });
    this.init(document);
  }

  init(root) {
    root.querySelectorAll('[data-menu-open]').forEach(button => { button.hidden = false; });
    root.querySelectorAll('[data-variant-select]').forEach((select) => this.updateVariant(select, false));
    root.querySelectorAll('input[type="date"][data-lead-days]').forEach((input) => {
      const days = Number(input.dataset.leadDays);
      if (!Number.isInteger(days) || days < 0) return;
      // Calendar fields, not ISO/UTC: California evening visitors keep the correct day.
      const date = new Date();
      date.setDate(date.getDate() + days);
      input.min = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
    });
    root.querySelectorAll('[data-preserve-form]').forEach((form) => this.restoreForm(form));
    root.querySelectorAll('[data-cart-form]').forEach((form) => this.updateCartState(form));
    root.querySelector('[data-form-result]')?.focus();
  }

  onClick(event) {
    const target = event.target.closest('button, a, [data-mobile-drawer]');
    if (!target) return;
    if (target.matches('[data-menu-open]')) this.openDrawer(target);
    if (target.matches('[data-menu-close]') || event.target.matches('[data-mobile-drawer]')) this.closeDrawer();
    if (target.matches('a[href]') && this.drawerState?.drawer.contains(target)) {
      this.closeDrawer(false);
      const url = new URL(target.href);
      if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search && url.hash) {
        const destination = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (destination) { destination.tabIndex = -1; destination.focus({preventScroll:true}); }
      }
    }
    if (target.matches('[data-decrease], [data-increase]')) {
      const input = target.closest('[data-quantity]')?.querySelector('input');
      if (!input) return;
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step) > 0 ? Number(input.step) : 1;
      const current = Number.isFinite(input.valueAsNumber) ? input.valueAsNumber : min;
      input.value = Math.min(max, Math.max(min, current + (target.matches('[data-decrease]') ? -step : step)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (target.matches('[data-gallery-thumb]')) {
      this.showImage(target.closest('[data-product-section]'), target.dataset.mediaId);
      target.closest('[data-product-section]')?.querySelector('[data-product-image]')?.focus();
    }
  }

  openDrawer(opener) {
    const drawer = document.getElementById(opener.getAttribute('aria-controls'));
    if (!drawer) return;
    this.closeDrawer(false);
    const background = [...document.querySelectorAll('main, footer, .site-header')];
    this.drawerState = { drawer, opener, background: background.map((el) => [el, el.inert]) };
    background.forEach((el) => { el.inert = true; });
    drawer.hidden = false;
    document.body.classList.add('drawer-open');
    opener.setAttribute('aria-expanded', 'true');
    drawer.querySelector('[data-menu-close]')?.focus();
  }

  closeDrawer(focus = true) {
    if (!this.drawerState) return;
    const { drawer, opener, background } = this.drawerState;
    drawer.hidden = true;
    background.forEach(([el, inert]) => { el.inert = inert; });
    opener.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    this.drawerState = null;
    if (focus && opener.isConnected) opener.focus();
  }

  onKeydown(event) {
    if (!this.drawerState) return;
    if (event.key === 'Escape') { event.preventDefault(); this.closeDrawer(); return; }
    if (event.key !== 'Tab') return;
    const items = [...this.drawerState.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')].filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  showImage(section, id) {
    if (!section || !id) return;
    const thumbs = [...section.querySelectorAll('[data-gallery-thumb]')];
    const selected = thumbs.find((el) => el.dataset.mediaId === String(id));
    const image = section.querySelector('[data-product-image]');
    if (!selected || !image) return;
    image.src = selected.dataset.src;
    image.srcset = selected.dataset.srcset || '';
    image.alt = selected.dataset.alt || '';
    image.width = Number(selected.dataset.width);
    image.height = Number(selected.dataset.height);
    thumbs.forEach((el) => el.setAttribute('aria-pressed', String(el === selected)));
  }

  updateVariant(select, updateUrl) {
    const section = select.closest('[data-product-section]');
    const option = select.options[select.selectedIndex];
    if (!section || !option) return;
    section.querySelectorAll('[data-product-link]').forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('variant', option.value);
      link.href = url.href;
    });
    const price = section.querySelector('[data-product-price]');
    const compare = section.querySelector('[data-compare-price]');
    const button = section.querySelector('[data-add-to-cart]');
    if (price) price.textContent = option.dataset.price;
    if (compare) { compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare; }
    if (button) {
      button.disabled = option.dataset.available !== 'true';
      button.textContent = button.disabled ? 'Sold out' : 'Add to cart';
    }
    const portion = section.querySelector('[data-variant-portion]');
    const orderButton = section.querySelector('[data-order-now]');
    if (orderButton) orderButton.disabled = option.dataset.available !== 'true';
    if (portion) { portion.textContent = option.dataset.portion || ''; portion.hidden = !option.dataset.portion; }
    const quantity = section.querySelector('[name="quantity"]');
    if (quantity) {
      quantity.min = option.dataset.min || '1';
      quantity.step = option.dataset.step || '1';
      quantity.max = option.dataset.max || '';
      const min = Number(quantity.min), step = Number(quantity.step);
      const value = Math.max(min, Number(quantity.value) || min);
      quantity.value = Math.min(quantity.max ? Number(quantity.max) : Infinity, min + Math.floor((value - min) / step) * step);
    }
    this.showImage(section, option.dataset.mediaId || section.dataset.defaultMedia);
    if (updateUrl && section.dataset.updateUrl === 'true') {
      const url = new URL(location.href);
      url.searchParams.set('variant', option.value);
      history.replaceState({}, '', url);
    }
  }

  updateCartState(form) {
    if (!form) return;
    const dirty = [...form.querySelectorAll('[data-cart-quantity]')].some(input => input.value !== input.dataset.savedQuantity);
    const notice = form.querySelector('[data-cart-update-notice]');
    const update = form.querySelector('[data-cart-update]');
    form.toggleAttribute('data-cart-dirty', dirty);
    if (notice) notice.textContent = dirty ? 'Quantity changed. Update your cart to refresh the totals below.' : '';
    if (update) update.classList.toggle('button--secondary', !dirty);
    const checkout = form.querySelector('[name="checkout"]');
    if (checkout) checkout.disabled = dirty;
  }

  formKey(form) {
    // Per-tab, per-page and per-form, not an enduring browser draft.
    const view = new URL(location.href).searchParams.get('view') || '';
    return ['psb:form', location.pathname, view, form.id].join(':');
  }

  restoreForm(form) {
    // pageshow/editor events must not overwrite corrections made after an error.
    if (this.restoredForms.has(form)) return;
    this.restoredForms.add(form);
    const key = this.formKey(form);
    try {
      if (form.querySelector('[data-form-success]')) { sessionStorage.removeItem(key); return; }
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!saved) return;
      if (!Number.isFinite(saved.at) || saved.at > Date.now() || Date.now() - saved.at > 20 * 60 * 1000) { sessionStorage.removeItem(key); return; }
      if (!form.querySelector('.form-errors')) return;
      for (const input of form.elements) {
        if (input.type === 'hidden' || !input.name?.startsWith('contact[')) continue;
        if (Object.hasOwn(saved.values || {}, input.name)) {
          if (input.type === 'radio' || input.type === 'checkbox') input.checked = input.value === saved.values[input.name];
          else input.value = saved.values[input.name];
        }
      }
      form.dispatchEvent(new CustomEvent('psb:form-restored', {bubbles:true}));
    } catch (_) { /* Storage unavailable: native returned values still work. */ }
  }

  onSubmit(event) {
    const form = event.target;
    if (form.matches('[data-review-blocked="true"]')) {
      event.preventDefault();
      form.querySelector('[data-review-notice]')?.focus();
      return;
    }
    if (event.defaultPrevented) return;
    if (!form.matches('[data-preserve-form]')) return;
    // Only save at submission; never log or transmit a second copy of contact data.
    try {
      const values = {};
      for (const input of form.elements) {
        if (input.type === 'hidden' || input.disabled || !input.name?.startsWith('contact[')) continue;
        if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) continue;
        values[input.name] = input.value;
      }
      sessionStorage.setItem(this.formKey(form), JSON.stringify({ at: Date.now(), values }));
    } catch (_) { /* Native submission must work with storage disabled. */ }
  }
}

const startPlentySweet = () => {
  if (!window.plentySweetTheme) window.plentySweetTheme = new PlentySweetTheme();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startPlentySweet, { once: true });
else startPlentySweet();
